import { urlUploadDataStore } from './useUrlUploadDataStore';

export function useUrlDataCollector() {
  type InstanceData = {
    url: string;
    prefixId?: number | string;
    prefixCode?: string;
    numbering: number;
    sequential: number;
    changelog: string;
    managerId?: number | string;
  };

  const getPrefixCode = (data: InstanceData): string | undefined => {
    if (data.prefixCode) {
      return data.prefixCode;
    }

    if (typeof data.prefixId === 'number') {
      return urlUploadDataStore.getPrefixCode(data.prefixId);
    }

    return data.prefixId;
  };

  const buildFileName = (prefixCode: string, data: InstanceData): string => {
    const extension = urlUploadDataStore.getUrlExtension(data.url);
    const numberingStr = data.numbering.toString().padStart(2, '0');
    return `${prefixCode}${numberingStr}.${data.sequential}.${extension}`;
  };

  const normalizeUrl = (url: string): string => {
    return url.startsWith('http') ? url : `https://${url}`;
  };

  const normalizeManagerId = (managerId: number | string): number => {
    return typeof managerId === 'number' ? managerId : Number.parseInt(String(managerId), 10);
  };

  const processInstance = (
    instance: { id: number; isValid: boolean },
    getInstanceData: (id: number) => InstanceData | null
  ): {
    url: string;
    name: string;
    changelog: string;
    managerId: number;
  } | null => {
    const data = getInstanceData(instance.id);
    if (!data?.prefixId) {
      return null;
    }

    const prefixCode = getPrefixCode(data);
    if (!prefixCode) {
      return null;
    }

    const fileName = buildFileName(prefixCode, data);
    if (!fileName || !data.managerId) {
      return null;
    }

    return {
      url: normalizeUrl(data.url),
      name: fileName,
      changelog: data.changelog,
      managerId: normalizeManagerId(data.managerId),
    };
  };

  const collectInternalUrlData = (
    instances: Array<{ id: number; isValid: boolean }>,
    getInstanceData: (id: number) => InstanceData | null
  ): Array<{
    url: string;
    name: string;
    changelog: string;
    managerId: number;
  }> => {
    const validInstances = instances.filter((instance) => instance.isValid);
    const urlData: Array<{
      url: string;
      name: string;
      changelog: string;
      managerId: number;
    }> = [];

    for (const instance of validInstances) {
      const processed = processInstance(instance, getInstanceData);
      if (processed) {
        urlData.push(processed);
      }
    }

    return urlData;
  };

  const collectExternalUrlData = (
    instances: Array<{ id: number; isValid: boolean }>,
    getInstanceData: (id: number) => {
      url: string;
      documentName: string;
    } | null,
    externalDocumentName: string
  ): Array<{
    url: string;
    name: string;
  }> => {
    const validInstances = instances.filter((instance) => instance.isValid);
    const urlData: Array<{
      url: string;
      name: string;
    }> = [];

    for (const instance of validInstances) {
      const data = getInstanceData(instance.id);
      if (!data) continue;

      const extension = urlUploadDataStore.getUrlExtension(data.url);
      const fileName = urlUploadDataStore.generateExternalFileName(
        data.documentName || externalDocumentName,
        extension
      );

      urlData.push({
        url: data.url.startsWith('http') ? data.url : `https://${data.url}`,
        name: fileName,
      });
    }

    return urlData;
  };

  return {
    collectInternalUrlData,
    collectExternalUrlData,
  };
}

